import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { CheckCircle2, XCircle } from 'lucide-react';

// ---------- shared helpers ----------

export const formatFieldLabel = (fieldId) =>
  fieldId.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ').trim();

export const isEmptyValue = (value) => {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') {
    if (value.fileName) return false;
    if (value.downloaded !== undefined) return false;
    return true;
  }
  return false;
};

export const formatCustomValue = (value) => {
  if (isEmptyValue(value)) return null;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') {
    if (value.fileName) return value.fileName;
    if (value.downloaded !== undefined) return value.downloaded ? 'Downloaded' : 'Not downloaded';
    return null;
  }
  return String(value);
};

/**
 * Resolve the admin-configured layout against the actual fields/values.
 * Unconfigured fields (added after the layout was saved) fall back to:
 * visible, first section, plain value display.
 */
export function resolveOverviewLayout(layout, valueKeys = []) {
  const sections = layout?.sections?.length
    ? layout.sections
    : [{ id: 'default', title: 'Additional Information' }];
  const defs = layout?.field_defs || [];
  const defMap = {};
  defs.forEach((d) => { defMap[d.id] = d; });

  const configured = layout?.fields || [];
  const configuredIds = new Set(configured.map((f) => f.field_id));
  const universe = [...configured];
  const allIds = [...new Set([...defs.map((d) => d.id), ...valueKeys])];
  allIds.forEach((id) => {
    if (!configuredIds.has(id)) {
      // Built-ins stay in the Details card until the admin explicitly configures them
      if (defMap[id]?.builtin) return;
      universe.push({ field_id: id, visible: true, placement: 'section', section_id: sections[0].id, display: 'value' });
    }
  });

  const validSectionIds = new Set(sections.map((s) => s.id));
  const sectionOf = (f) => (f.section_id && validSectionIds.has(f.section_id) ? f.section_id : sections[0].id);

  return {
    infoBox: universe.filter((f) => f.visible && f.placement === 'info_box'),
    details: universe.filter((f) => f.visible && f.placement === 'details'),
    sections: sections.map((s) => ({
      ...s,
      fields: universe.filter(
        (f) => f.visible && f.placement !== 'info_box' && f.placement !== 'details' && sectionOf(f) === s.id
      ),
    })),
    defMap,
  };
}

const labelFor = (fieldId, defMap) => defMap[fieldId]?.label || formatFieldLabel(fieldId);

/**
 * Documents-tab sync: a document whose type matches a file-upload field's label
 * (case-insensitive) counts as that field being uploaded. Latest version wins.
 */
const buildDocTypeMap = (documents = []) => {
  const map = {};
  documents.forEach((d) => {
    if (!d?.doc_type || d.is_deleted) return;
    const key = d.doc_type.trim().toLowerCase();
    if (!map[key] || (d.version || 0) >= (map[key].version || 0)) map[key] = d;
  });
  return map;
};

const effectiveValue = (f, defMap, values, docMap) => {
  const def = defMap[f.field_id];
  if (def?.type === 'file_upload' && def.label) {
    const doc = docMap[def.label.trim().toLowerCase()];
    if (doc) return { fileName: doc.filename };
  }
  return values[f.field_id];
};

export function FieldValueDisplay({ value, display, fieldType }) {
  if (display === 'check') {
    const has = !isEmptyValue(value);
    const isFile = fieldType === 'file_upload';
    return has ? (
      <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
        <CheckCircle2 className="w-4 h-4" /> {isFile ? 'Uploaded' : 'Provided'}
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 text-slate-400">
        <XCircle className="w-4 h-4" /> {isFile ? 'Not uploaded' : 'Not provided'}
      </span>
    );
  }
  const d = formatCustomValue(value);
  return d === null ? null : <span className="font-medium text-slate-900">{d}</span>;
}

// ---------- Information-box rows (right card on the Overview tab) ----------

export function InfoBoxCustomFields({ layout, values = {}, documents = [], testidPrefix = 'overview' }) {
  const { infoBox, defMap } = resolveOverviewLayout(layout, Object.keys(values));
  const docMap = buildDocTypeMap(documents);
  const rows = infoBox
    .map((f) => ({ ...f, _value: effectiveValue(f, defMap, values, docMap) }))
    .filter((f) => f.display === 'check' || formatCustomValue(f._value) !== null);
  if (rows.length === 0) return null;
  return (
    <>
      {rows.map((f) => (
        <div key={f.field_id} data-testid={`${testidPrefix}-infobox-field-${f.field_id}`}>
          <p className="text-sm text-slate-500">{labelFor(f.field_id, defMap)}</p>
          <div className="mt-0.5">
            <FieldValueDisplay value={f._value} display={f.display} fieldType={defMap[f.field_id]?.type} />
          </div>
        </div>
      ))}
    </>
  );
}

// ---------- Details-card rows (left card on the Overview tab) ----------

export function DetailsCustomFields({ layout, values = {}, documents = [], testidPrefix = 'overview' }) {
  const { details, defMap } = resolveOverviewLayout(layout, Object.keys(values));
  const docMap = buildDocTypeMap(documents);
  const rows = details
    .map((f) => ({ ...f, _value: effectiveValue(f, defMap, values, docMap) }))
    .filter((f) => f.display === 'check' || formatCustomValue(f._value) !== null);
  if (rows.length === 0) return null;
  return (
    <>
      {rows.map((f) => (
        <div key={f.field_id} data-testid={`${testidPrefix}-details-field-${f.field_id}`}>
          <p className="text-sm text-slate-500">{labelFor(f.field_id, defMap)}</p>
          <div className="mt-1 text-slate-700">
            <FieldValueDisplay value={f._value} display={f.display} fieldType={defMap[f.field_id]?.type} />
          </div>
        </div>
      ))}
    </>
  );
}

// ---------- Section cards (below the Details / Information cards) ----------

export function CustomFieldsSections({ layout, values = {}, documents = [], testidPrefix = 'overview' }) {
  const { sections, defMap } = resolveOverviewLayout(layout, Object.keys(values));
  const docMap = buildDocTypeMap(documents);
  const renderable = sections
    .map((s) => ({
      ...s,
      fields: s.fields
        .map((f) => ({ ...f, _value: effectiveValue(f, defMap, values, docMap) }))
        .filter((f) => f.display === 'check' || formatCustomValue(f._value) !== null),
    }))
    .filter((s) => s.fields.length > 0);
  if (renderable.length === 0) return null;
  return (
    <>
      {renderable.map((s) => (
        <Card key={s.id} data-testid={`${testidPrefix}-section-${s.id}`}>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">{s.title}</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-4">
              {s.fields.map((f) => (
                <div key={f.field_id} className="space-y-1" data-testid={`custom-field-value-${f.field_id}`}>
                  <dt className="text-sm text-slate-500">{labelFor(f.field_id, defMap)}</dt>
                  <dd>
                    <FieldValueDisplay value={f._value} display={f.display} fieldType={defMap[f.field_id]?.type} />
                  </dd>
                </div>
              ))}
            </dl>
          </CardContent>
        </Card>
      ))}
    </>
  );
}
